// Shared strategy-filter state for the aHedge investor dashboard. One
// multi-select drives the WHOLE page: KPIs, chart, positions, P&L all
// scope to the selected strategies. `selection === null` means "all
// active" (the default — auto-includes newly-deployed strategies).
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { portfolioApi, type PortfolioStrategyOption } from './api';

const STORAGE_KEY = 'aHedge.investorFilter';

interface FilterValue {
  strategies: PortfolioStrategyOption[];
  isLoading: boolean;
  /** null = all active. */
  selection: number[] | null;
  /** ids to pass to the API (undefined when all → API returns all active). */
  queryIds: number[] | undefined;
  isAll: boolean;
  selectedCount: number;
  isSelected: (id: number) => boolean;
  toggle: (id: number) => void;
  selectAll: () => void;
}

const Ctx = createContext<FilterValue | null>(null);

function loadInitial(): number[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null || raw === 'all') return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((n: unknown) => typeof n === 'number') : null;
  } catch {
    return null;
  }
}

export function InvestorFilterProvider({ children }: { children: ReactNode }) {
  const { data: strategies = [], isLoading } = useQuery({
    queryKey: ['portfolio-strategies'],
    queryFn: portfolioApi.strategies,
    refetchInterval: 30_000,
  });

  const [selection, setSelection] = useState<number[] | null>(loadInitial);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, selection === null ? 'all' : JSON.stringify(selection));
    } catch {
      /* ignore quota / private mode */
    }
  }, [selection]);

  // Drop ids no longer active; collapse a full explicit set back to "all".
  useEffect(() => {
    if (selection === null || strategies.length === 0) return;
    const active = new Set(strategies.map((s) => s.id));
    const cleaned = selection.filter((id) => active.has(id));
    if (cleaned.length === 0 || cleaned.length === strategies.length) setSelection(null);
    else if (cleaned.length !== selection.length) setSelection(cleaned);
  }, [strategies, selection]);

  const value = useMemo<FilterValue>(() => {
    const allIds = strategies.map((s) => s.id);
    const isAll = selection === null;
    const effective = isAll ? allIds : selection;
    const set = new Set(effective);
    return {
      strategies,
      isLoading,
      selection,
      queryIds: isAll ? undefined : selection,
      isAll,
      selectedCount: effective.length,
      isSelected: (id) => set.has(id),
      selectAll: () => setSelection(null),
      toggle: (id) =>
        setSelection((prev) => {
          const base = prev === null ? allIds : prev;
          const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
          if (next.length === 0) return prev; // always keep ≥1 selected
          if (next.length === allIds.length) return null; // back to "all"
          return next;
        }),
    };
  }, [strategies, isLoading, selection]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useInvestorFilter(): FilterValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useInvestorFilter must be used inside <InvestorFilterProvider>');
  return v;
}
